import { useEffect, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCheck, Megaphone, X } from "lucide-react";

const TYPE_COLORS = {
  success: "var(--success)",
  warning: "var(--pending)",
  danger:  "var(--danger)",
  info:    "var(--accent)",
};

const BROADCAST_TYPES = ["info", "warning", "success", "danger"];

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" });
}

export default function Notifications() {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [bForm, setBForm] = useState({ title: "", message: "", type: "info" });
  const [bSending, setBSending] = useState(false);
  const [bResult, setBResult] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => { fetchNotifs(); }, []);

  async function fetchNotifs() {
    setLoading(true);
    try {
      const res = await apiGet("/notifications");
      setNotifs(res.data);
    } catch (e) {} finally { setLoading(false); }
  }

  async function markAllRead() {
    try {
      await api("/notifications/read-all", { method: "PUT" });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) {}
  }

  async function markRead(id) {
    try {
      await api(`/notifications/${id}/read`, { method: "PUT" });
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (e) {}
  }

  async function sendBroadcast(e) {
    e.preventDefault();
    if (!bForm.title.trim() || !bForm.message.trim()) return;
    setBSending(true); setBResult("");
    try {
      const res = await apiPost("/notifications/broadcast", bForm);
      setBResult(res.data.message || "Broadcast sent!");
      setBForm({ title: "", message: "", type: "info" });
      setTimeout(() => { setShowBroadcast(false); setBResult(""); }, 2000);
    } catch (e) {
      setBResult("Failed to send broadcast.");
    } finally { setBSending(false); }
  }

  const unreadCount = notifs.filter(n => !n.is_read).length;

  return (
    <div>
      <div className="section-panel" data-testid="notifications-panel">
        <div className="section-head">
          <div className="section-title">
            Notifications {unreadCount > 0 && <span className="nav-badge" style={{ marginLeft: 8 }}>{unreadCount}</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => setShowBroadcast(true)} data-testid="broadcast-btn">
                <Megaphone size={12} /> Broadcast
              </button>
            )}
            {unreadCount > 0 && (
              <button className="btn" onClick={markAllRead} data-testid="mark-all-read-btn">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading notifications...</div>
        ) : notifs.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>No notifications</div>
        ) : (
          <div>
            {notifs.map(n => (
              <div
                key={n.id}
                className={`notif-item ${!n.is_read ? "unread" : ""}`}
                onClick={() => !n.is_read && markRead(n.id)}
                style={{ cursor: !n.is_read ? "pointer" : "default" }}
                data-testid={`notif-${n.id}`}
              >
                <div className="notif-dot-icon" style={{ background: n.is_read ? "var(--border)" : (TYPE_COLORS[n.type] || "var(--accent)") }} />
                <div>
                  <div className="notif-title" style={{ color: n.is_read ? "var(--text2)" : "var(--text)" }}>{n.title}</div>
                  <div className="notif-msg">{n.message}</div>
                  <div className="notif-time">{fmtTime(n.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Broadcast Modal */}
      {showBroadcast && (
        <div className="modal-overlay">
          <div className="modal-box" data-testid="broadcast-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div className="modal-title" style={{ margin: 0 }}>Broadcast to All Residents</div>
              <button className="btn" onClick={() => { setShowBroadcast(false); setBResult(""); }} style={{ padding: "3px 7px" }}>
                <X size={13} />
              </button>
            </div>
            {bResult && (
              <div className={`${bResult.startsWith("Failed") ? "auth-error" : "auth-success"}`} data-testid="broadcast-result">
                {bResult}
              </div>
            )}
            <form onSubmit={sendBroadcast}>
              <div className="form-group">
                <label className="form-label">Notification Type</label>
                <select className="form-input" value={bForm.type} onChange={e => setBForm(f => ({...f, type: e.target.value}))} data-testid="broadcast-type-select">
                  {BROADCAST_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" placeholder="e.g. Maintenance Notice" value={bForm.title} onChange={e => setBForm(f => ({...f, title: e.target.value}))} required data-testid="broadcast-title-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-input" rows={3} placeholder="Write your announcement..." value={bForm.message} onChange={e => setBForm(f => ({...f, message: e.target.value}))} required data-testid="broadcast-message-input" />
              </div>
              <div style={{ fontSize: 10, color: "var(--text2)", marginBottom: 12 }}>
                This will send to all <strong>active</strong> residents.
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => { setShowBroadcast(false); setBResult(""); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={bSending} data-testid="broadcast-submit-btn">
                  <Megaphone size={12} /> {bSending ? "Sending..." : "Send Broadcast"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
